// src/pages/FulfillmentPage/components/FulfillmentItemsList.tsx
import React from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Stack,
  Avatar,
  alpha,
  useTheme,
  Paper,
  Chip,
  Divider
} from "@mui/material";
import ShoppingBagIcon from "@mui/icons-material/ShoppingBag";
import InventoryIcon from "@mui/icons-material/Inventory";
import { FulfillmentItem } from "../types";
import { formatAttributes as formatAttributesRaw } from "../utils/helpers";

// ✅ protects against different attribute shapes:
// - [{ name, value }]
// - [{ name, values }]
// - object map { Size: "XL" }
// - JSON string
const safeFormatAttributes = (attributes: any) => {
  let attrs = attributes;

  if (typeof attrs === "string") {
    try {
      attrs = JSON.parse(attrs);
    } catch {
      // If it's just a plain string, return it
      return attrs.trim();
    }
  }

  if (Array.isArray(attrs)) {
    const out = attrs
      .map((a) => {
        const name = a?.name ?? "";
        const value =
          typeof a?.value === "string"
            ? a.value
            : Array.isArray(a?.values)
              ? a.values.join(", ")
              : typeof a?.values === "string"
                ? a.values
                : "";

        if (!name || !value) return "";
        return `${name}: ${value}`;
      })
      .filter(Boolean)
      .join("\n");

    return out;
  }

  if (attrs && typeof attrs === "object") {
    return Object.entries(attrs)
      .map(([k, v]) => {
        if (v == null || v === "") return "";
        return `${k}: ${Array.isArray(v) ? v.join(", ") : String(v)}`;
      })
      .filter(Boolean)
      .join("\n");
  }

  // fallback to existing helper (if it handles other cases)
  return formatAttributesRaw(attributes) || "";
};

interface FulfillmentItemsListProps {
  items: FulfillmentItem[];
}

export const FulfillmentItemsList: React.FC<FulfillmentItemsListProps> = ({ items }) => {
  const theme = useTheme();
  const itemCount = items?.length || 0;
  const totalQuantity = items?.reduce((sum, item) => sum + item.quantity, 0) || 0;

  return (
    <Paper
      elevation={0}
      sx={{
        borderRadius: 3,
        mb: 2,
        width: "100%",
        overflow: "hidden",
        border: '1px solid',
        borderColor: theme.palette.divider,
        bgcolor: theme.palette.background.paper,
        transition: 'all 0.2s ease',
        '&:hover': {
          borderColor: alpha(theme.palette.primary.main, 0.2),
          boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.05)}`
        }
      }}
    >
      {/* Header with colored accent */}
      <Box
        sx={{
          height: 4,
          width: '100%',
          bgcolor: theme.palette.primary.main,
          opacity: 0.8
        }}
      />

      <CardContent sx={{ p: { xs: 2.5, sm: 3 } }}>
        {/* Title Section with Summary */}
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          sx={{ mb: 3 }}
        >
          <Stack direction="row" alignItems="center" spacing={1.5}>
            <Avatar
              sx={{
                width: 40,
                height: 40,
                bgcolor: alpha(theme.palette.primary.main, 0.1),
                color: theme.palette.primary.main,
              }}
            >
              <ShoppingBagIcon />
            </Avatar>
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 700, lineHeight: 1.2 }}>
                Order Items
              </Typography>
              <Typography variant="caption" sx={{ color: theme.palette.text.secondary }}>
                {itemCount} {itemCount === 1 ? 'item' : 'items'} • {totalQuantity} units total
              </Typography>
            </Box>
          </Stack>
        </Stack>

        {/* Items List */}
        <Stack spacing={1.5}>
          {(items || []).length === 0 ? (
            <Paper
              variant="outlined"
              sx={{
                p: 4,
                textAlign: 'center',
                bgcolor: alpha(theme.palette.background.default, 0.5),
                borderRadius: 2,
                borderStyle: 'dashed'
              }}
            >
              <InventoryIcon
                sx={{
                  fontSize: 48,
                  color: theme.palette.text.disabled,
                  mb: 1
                }}
              />
              <Typography variant="body1" color="text.secondary" gutterBottom>
                No items found
              </Typography>
              <Typography variant="caption" color="text.disabled">
                This fulfillment has no items associated with it
              </Typography>
            </Paper>
          ) : (
            (items || []).map((item) => (
              <Paper
                key={item.id}
                variant="outlined"
                sx={{
                  p: 2,
                  borderRadius: 2,
                  bgcolor: theme.palette.background.paper,
                  borderColor: theme.palette.divider,
                  transition: 'all 0.2s ease',
                  '&:hover': {
                    borderColor: theme.palette.primary.main,
                    bgcolor: alpha(theme.palette.primary.main, 0.02),
                    transform: 'translateY(-1px)',
                    boxShadow: theme.shadows[2]
                  },
                  position: 'relative',
                  overflow: 'hidden'
                }}
              >
                {/* Item number indicator (for visual reference) */}
                <Box
                  sx={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: 4,
                    height: '100%',
                    bgcolor: theme.palette.primary.main,
                    opacity: 0.5
                  }}
                />

                <Box
                  sx={{
                    display: "flex",
                    gap: { xs: 1.5, sm: 2 },
                    alignItems: "flex-start",
                    width: "100%",
                    boxSizing: "border-box",
                    flexWrap: { xs: "wrap", sm: "nowrap" },
                    pl: 1
                  }}
                >
                  {/* Quantity Avatar */}
                  <Avatar
                    sx={{
                      width: 48,
                      height: 48,
                      bgcolor: alpha(theme.palette.primary.main, 0.1),
                      color: theme.palette.primary.main,
                      fontWeight: 700,
                      fontSize: "1.1rem",
                      flexShrink: 0,
                      border: `2px solid ${alpha(theme.palette.primary.main, 0.2)}`,
                      transition: 'transform 0.2s ease',
                      '&:hover': {
                        transform: 'scale(1.05)',
                        borderColor: theme.palette.primary.main
                      }
                    }}
                  >
                    {item.quantity}
                  </Avatar>

                  {/* Item Details */}
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    {/* On mobile the name + variant chip stack vertically so a long
                        variant label can't squeeze the name column to near-zero
                        width (which made the name break one letter per line). */}
                    <Stack
                      direction={{ xs: "column", sm: "row" }}
                      alignItems={{ xs: "flex-start", sm: "center" }}
                      spacing={1}
                      sx={{ mb: 0.5, flexWrap: "wrap" }}
                    >
                      <Typography
                        sx={{
                          fontWeight: 700,
                          fontSize: '1rem',
                          minWidth: 0,
                          wordBreak: "break-word",
                          color: theme.palette.text.primary
                        }}
                      >
                        {item.product_name}
                      </Typography>
                      {(() => {
                        const vn = (item.variant_name ?? "").toString().trim();
                        const show = vn && vn.toLowerCase() !== "undefined" && vn.toLowerCase() !== "null";
                        return show ? (
                          <Chip
                            label={vn}
                            size="small"
                            sx={{
                              height: 20,
                              fontSize: '0.7rem',
                              bgcolor: alpha(theme.palette.secondary.main, 0.1),
                              color: theme.palette.secondary.main,
                              borderRadius: 1
                            }}
                          />
                        ) : null;
                      })()}
                    </Stack>

                    {/* Attributes */}
                    {(() => {
                      const attrsText = safeFormatAttributes(item.attributes);
                      if (!attrsText) return null;

                      return (
                        <Typography
                          variant="caption"
                          sx={{
                            color: theme.palette.text.secondary,
                            lineHeight: 1.3,
                            wordBreak: "break-word",
                            whiteSpace: "pre-line",
                            display: 'block',
                            mb: 1,
                            fontFamily: 'monospace',
                            bgcolor: alpha(theme.palette.background.default, 0.5),
                            p: 0.5,
                            px: 1,
                            borderRadius: 1,
                            width: 'fit-content'
                          }}
                        >
                          {attrsText}
                        </Typography>
                      );
                    })()}

                    {/* Item ID for reference */}
                    <Typography variant="caption" sx={{ color: theme.palette.text.disabled }}>
                      Item ID: {item.id}
                    </Typography>
                  </Box>

                  {/* Unit Price - Only showing unit price now */}
                  <Box sx={{
                    textAlign: "right",
                    minWidth: { xs: "100%", sm: 100 },
                    mt: { xs: 1, sm: 0 },
                    flexShrink: 0,
                    bgcolor: alpha(theme.palette.background.default, 0.5),
                    p: 1,
                    borderRadius: 1.5,
                    border: '1px solid',
                    borderColor: alpha(theme.palette.divider, 0.5)
                  }}>
                    <Typography variant="caption" sx={{ color: theme.palette.text.secondary, display: 'block' }}>
                      Unit Price
                    </Typography>
                    <Typography sx={{ fontWeight: 700, fontSize: "1.2rem", color: theme.palette.success.main }}>
                      ${Number(item.price || 0).toFixed(2)}
                    </Typography>
                    <Typography variant="caption" sx={{ color: theme.palette.text.secondary, display: 'block', mt: 0.5 }}>
                      × {item.quantity} {item.quantity === 1 ? 'unit' : 'units'}
                    </Typography>
                  </Box>
                </Box>
              </Paper>
            ))
          )}
        </Stack>

        {/* Footer Summary - Removed order total, just showing counts */}
        {itemCount > 0 && (
          <Paper
            variant="outlined"
            sx={{
              mt: 2,
              p: 2,
              borderRadius: 2,
              bgcolor: alpha(theme.palette.primary.main, 0.02),
              borderColor: alpha(theme.palette.primary.main, 0.1),
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center'
            }}
          >
            <Box>
              <Typography variant="caption" sx={{ color: theme.palette.text.secondary, display: 'block' }}>
                Total Items
              </Typography>
              <Typography sx={{ fontWeight: 700, fontSize: '1.3rem', color: theme.palette.primary.main }}>
                {itemCount}
              </Typography>
            </Box>

            <Divider orientation="vertical" flexItem sx={{ mx: 2 }} />

            <Box>
              <Typography variant="caption" sx={{ color: theme.palette.text.secondary, display: 'block' }}>
                Total Units
              </Typography>
              <Typography sx={{ fontWeight: 700, fontSize: '1.3rem', color: theme.palette.primary.main }}>
                {totalQuantity}
              </Typography>
            </Box>
          </Paper>
        )}
      </CardContent>
    </Paper>
  );
};